// Shared number rules for every form that talks to the assets service.
// Amounts travel as strings so no precision is lost, and the service stores
// them with up to 18 decimal places.

export const MAX_FRACTION_DIGITS = 18;

const DECIMAL = /^\d+(\.\d+)?$/;
const INTEGER = /^-?\d+$/;
// Largest unscaled value the service accepts (a signed 64-bit integer).
const INT64_MAX = "9223372036854775807";

export const asTrimmedString = (value) => String(value === null || value === undefined ? "" : value).trim();

// "1000000.000000000000000000" -> "1000000", "0.010000000000000000" -> "0.01".
// Works on the string itself, so nothing is rounded.
export const trimDecimal = (value) => {
  const text = asTrimmedString(value);
  if (!text.includes(".")) return text;
  return text.replace(/0+$/, "").replace(/\.$/, "");
};

// Returns a message, or null when the value is fine.
export function decimalProblem(value, label, { allowZero = false } = {}) {
  const text = asTrimmedString(value);
  if (!DECIMAL.test(text)) return `${label} must be a number, like 0.01`;

  const [whole, fraction = ""] = text.split(".");
  if (fraction.length > MAX_FRACTION_DIGITS) {
    return `${label} can have at most ${MAX_FRACTION_DIGITS} decimal places`;
  }

  const unscaled = (whole + fraction).replace(/^0+/, "");
  if (!unscaled) return allowZero ? null : `${label} must be greater than 0`;
  if (unscaled.length > INT64_MAX.length || (unscaled.length === INT64_MAX.length && unscaled > INT64_MAX)) {
    return `${label} is too large`;
  }
  return null;
}

export function integerProblem(value, { label, min, max }) {
  const text = asTrimmedString(value);
  if (!INTEGER.test(text)) return `${label} must be a whole number`;

  const number = Number(text);
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;
  if (hasMin && hasMax && (number < min || number > max)) return `${label} must be between ${min} and ${max}`;
  if (hasMin && number < min) return `${label} must be ${min} or more`;
  if (hasMax && number > max) return `${label} must be ${max} or less`;
  return null;
}

// Exact comparison of two valid decimal strings, with no floating point
// rounding. Returns -1, 0 or 1.
export function compareDecimals(a, b) {
  const [aWhole, aFraction = ""] = asTrimmedString(a).split(".");
  const [bWhole, bFraction = ""] = asTrimmedString(b).split(".");
  const width = Math.max(aFraction.length, bFraction.length);
  const aDigits = (aWhole + aFraction.padEnd(width, "0")).replace(/^0+/, "");
  const bDigits = (bWhole + bFraction.padEnd(width, "0")).replace(/^0+/, "");
  if (aDigits.length !== bDigits.length) return aDigits.length < bDigits.length ? -1 : 1;
  if (aDigits === bDigits) return 0;
  return aDigits < bDigits ? -1 : 1;
}

// True when min is above max, i.e. the pair is the wrong way round.
export const isMinAboveMax = (min, max) => compareDecimals(min, max) > 0;
